import { zodResolver } from '@hookform/resolvers/zod';
import {
  DataGrid,
  Dialog,
  FilterToolbar,
  Form,
  FormButton,
  Input,
  number,
  SelectInput,
  string,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  GetAdminIntegrationsParams,
  useDeleteAdminIntegration,
  useGetAdminIntegrations,
  useGetAdminIntegrationTypes,
  usePostAdminIntegration
} from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';

import { IntegrationActionsCell, LinkButton } from '../../components';

type Integration = {
  description: string;
  integrationId: string;
  integrationTypeId?: string;
  IntegrationTypes?: { description?: string };
};

const createResolver = zodResolver(
  z.object({
    description: string({ required: true }),
    integrationTypeId: string({ required: true }),
    rateLimitBatchSize: number({ min: 0 }),
    rateLimitMaxParallelCalls: number({ min: 0 }),
    rateLimitTimeout: number({ min: 0 })
  })
);

type CreateFormProps = {
  configurationPayload: string;
  description: string;
  integrationTypeId: string;
  rateLimitBatchSize: number;
  rateLimitMaxParallelCalls: number;
  rateLimitTimeout: number;
};

const Page = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const deleteIntegration = useDeleteAdminIntegration({
    mutation: {
      onError: () => notifications.error('Failed to delete integration'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['/api/v3/admin/integrations']
        });
        notifications.success('Integration deleted');
      }
    }
  });

  const columns: GridColDef<Integration>[] = [
    {
      field: 'description',
      flex: 2,
      headerName: 'Description',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.description)}
          params={{ integrationId: params.row.integrationId }}
          to='/integrations/$integrationId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'integrationTypeId',
      flex: 1,
      headerName: 'Integration Type',
      sortable: false,
      valueGetter: (_value, row) =>
        row.IntegrationTypes?.description ?? row.integrationTypeId
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <IntegrationActionsCell
          integration={params.row}
          onDeleted={() =>
            deleteIntegration.mutate({
              integrationId: params.row.integrationId
            })
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];
  const [query, setQuery] = useState<GridQuery>();
  const [createDialogOpen, toggleCreateDialogOpen, setCreateDialogOpen] =
    useToggle(false);

  const filters: FilterDef[] = [
    { label: 'Search', name: 'search', type: 'text' }
  ];

  const integrations = useGetAdminIntegrations(
    query as GetAdminIntegrationsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const integrationTypes = useGetAdminIntegrationTypes();

  const createDefaultValues = {
    configurationPayload: '{}',
    description: '',
    integrationTypeId: '',
    rateLimitBatchSize: 0,
    rateLimitMaxParallelCalls: 0,
    rateLimitTimeout: 0
  };

  const createMethods = useForm<CreateFormProps>({
    defaultValues: createDefaultValues,
    mode: 'all',
    resolver: createResolver
  });

  const postIntegration = usePostAdminIntegration({
    mutation: {
      onError: () => notifications.error('Failed to create integration'),
      onSuccess: result => {
        queryClient.invalidateQueries({
          queryKey: ['/api/v3/admin/integrations']
        });
        setCreateDialogOpen(false);
        createMethods.reset(createDefaultValues);
        notifications.success('Integration created');
        navigate({ to: `/integrations/${result.integrationId}` });
      }
    }
  });

  const onCreateSubmit = (data: CreateFormProps) => {
    postIntegration.mutate({ data });
  };

  const onOpenCreateDialog = () => {
    createMethods.reset(createDefaultValues);
    setCreateDialogOpen(true);
  };

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>Integrations</Typography>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.integrationId}
        initialSort={[{ field: 'description', sort: 'asc' }]}
        loading={!query || integrations.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={integrations.data?.pagination?.total ?? 0}
        rows={integrations.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'integrations'
          },
          toolbar: {
            addLabel: 'Integration',
            addTestId: 'createIntegration',
            onAdd: onOpenCreateDialog,
            onRefresh: integrations.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'integrations-FilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <Dialog
        data-testid='createIntegrationDialog'
        fullWidth
        isOpen={createDialogOpen}
        maxWidth='sm'
        onClose={toggleCreateDialogOpen}>
        <Form
          methods={createMethods}
          noValidate
          onSubmit={createMethods.handleSubmit(onCreateSubmit)}>
          <DialogTitle variant='h3'>Create Integration</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Input
                data-testid='createDescription'
                format='string'
                fullWidth
                hideError
                label='Description'
                name='description'
                required
                size='small'
              />
              <SelectInput
                fullWidth
                hideError
                label='Integration Type'
                name='integrationTypeId'
                options={(integrationTypes.data ?? []).map(t => ({
                  label: t.description || t.typeName,
                  value: t.integrationTypeId
                }))}
                required
                size='small'
                variant='outlined'
              />
              <Input
                data-testid='createRateLimitTimeout'
                format='number'
                fullWidth
                hideError
                label='Rate Limit Timeout'
                min={0}
                name='rateLimitTimeout'
                size='small'
              />
              <Input
                data-testid='createRateLimitMaxParallelCalls'
                format='number'
                fullWidth
                hideError
                label='Rate Limit Max Parallel Calls'
                min={0}
                name='rateLimitMaxParallelCalls'
                size='small'
              />
              <Input
                data-testid='createRateLimitBatchSize'
                format='number'
                fullWidth
                hideError
                label='Rate Limit Batch Size'
                min={0}
                name='rateLimitBatchSize'
                size='small'
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelCreate'
              onClick={toggleCreateDialogOpen}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='saveCreate'
              triggerValidation
              variant='contained'>
              Save
            </FormButton>
          </DialogActions>
        </Form>
      </Dialog>
    </Stack>
  );
};

export const Route = createFileRoute('/integrations/')({ component: Page });
